import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import { eurShort, num } from "@/lib/format";
import { toast } from "sonner";

const COLORI = ["#DC2626", "#0EA5E9", "#16A34A", "#F59E0B", "#7C3AED", "#DB2777", "#0F766E"];
const ANNO = 2026;
const searchCls = "border border-slate-200 rounded-md px-3 py-2 text-sm bg-white text-slate-700 " +
  "focus:outline-none focus:ring-2 focus:ring-sky-300 transition-colors duration-200";

export default function MappaConvenzionati() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [scoperti, setScoperti] = useState(true);
  const [soloScoperti, setSoloScoperti] = useState(false);
  const [comuneSel, setComuneSel] = useState(null);
  const [qDealer, setQDealer] = useState("");
  const [qCap, setQCap] = useState("");
  const [dealerSel, setDealerSel] = useState(null);
  const [capSel, setCapSel] = useState("");

  const box = useRef(null);
  const mappa = useRef(null);
  const layer = useRef(null);

  useEffect(() => {
    api.get(`/dealers-mappa?anno=${ANNO}${sel.length ? `&commerciali=${sel.join(",")}` : ""}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [sel]);

  const colore = useMemo(() => {
    const m = {};
    (data?.commerciali || []).forEach((c, i) => { m[c.slug] = COLORI[i % COLORI.length]; });
    return m;
  }, [data]);

  useEffect(() => {
    if (!box.current || mappa.current) return;
    mappa.current = L.map(box.current, { center: [45.6, 9.5], zoom: 9, scrollWheelZoom: true });
    // tile OpenStreetMap standard: nessuna API key, nessuna scritta sovrapposta
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png",
      { attribution: "&copy; OpenStreetMap", maxZoom: 19 })
      .addTo(mappa.current);
    layer.current = L.layerGroup().addTo(mappa.current);
    return () => { mappa.current?.remove(); mappa.current = null; };
  }, []);

  const tuttiDealer = useMemo(() => {
    const out = [];
    (data?.punti || []).forEach((p) => p.dealer.forEach((d) => out.push({
      ...d, cap: p.cap, comune: p.comune, provincia: p.provincia, lat: p.lat, lng: p.lng,
      commerciale_nome: p.commerciale_nome, commerciale_slug: p.commerciale_slug })));
    return out.sort((a, b) => (a.ragione_sociale || "").localeCompare(b.ragione_sociale || ""));
  }, [data]);

  const comuniDisponibili = useMemo(
    () => [...new Set((data?.punti || []).map((p) => p.comune))].sort((a, b) => a.localeCompare(b)),
    [data]);

  const cercaDealer = () => {
    const v = qDealer.trim().toLowerCase();
    if (!v) return;
    const d = tuttiDealer.find((x) => (x.ragione_sociale || "").toLowerCase() === v)
      || tuttiDealer.find((x) => (x.ragione_sociale || "").toLowerCase().includes(v))
      || tuttiDealer.find((x) => String(x.codice_dealer) === v);
    if (!d) return toast.error(`Nessun convenzionato trovato per "${qDealer}"`);
    setCapSel(""); setQCap(""); setComuneSel(null); setSoloScoperti(false);
    setDealerSel(d);
  };

  const cercaComune = () => {
    const v = qCap.trim().toLowerCase();
    if (!v) return;
    const trovato = (data?.punti || []).find((p) => (p.comune || "").toLowerCase() === v)
      || (data?.punti || []).find((p) => (p.comune || "").toLowerCase().includes(v));
    if (!trovato) return toast.error(`Nessun convenzionato nel comune "${qCap}"`);
    setDealerSel(null); setQDealer(""); setComuneSel(null); setSoloScoperti(false);
    setCapSel(trovato.comune);
  };

  const azzeraRicerca = () => { setQDealer(""); setQCap(""); setDealerSel(null); setCapSel(""); };

  const puntiVisibili = useMemo(() => {
    let p = data?.punti || [];
    if (capSel) p = p.filter((x) => x.comune === capSel);
    if (dealerSel) p = p.filter((x) => x.dealer.some((d) => d.codice_dealer === dealerSel.codice_dealer));
    return p;
  }, [data, capSel, dealerSel]);

  const dealerComune = useMemo(() => {
    if (!capSel) return [];
    const out = [];
    puntiVisibili.forEach((p) => p.dealer.forEach((d) => out.push({
      ...d, cap: p.cap, commerciale_nome: p.commerciale_nome, commerciale_slug: p.commerciale_slug })));
    return out.sort((a, b) => (b.importo || 0) - (a.importo || 0) || b.pratiche - a.pratiche);
  }, [capSel, puntiVisibili]);

  useEffect(() => {
    if (!layer.current || !data) return;
    layer.current.clearLayers();
    const punti = puntiVisibili;
    const ricerca = Boolean(capSel || dealerSel);
    const max = punti.reduce((m, p) => Math.max(m, p.importo), 0);

    if (!ricerca && (scoperti || soloScoperti)) {
      (data.scoperti || []).forEach((s) => {
        L.circleMarker([s.lat, s.lng], { radius: 5, color: "#94A3B8", fillColor: "#94A3B8",
          fillOpacity: 0.5, weight: 1 })
          .bindTooltip(`${s.comune} (${s.provincia}) · nessun convenzionato`)
          .bindPopup(`<b>${s.comune} · ${s.provincia}</b><br/>CAP ${s.cap}${s.cap_totali > 1 ? ` (+${s.cap_totali - 1})` : ""}`
            + `${s.popolazione ? `<br/>${num(s.popolazione)} abitanti` : ""}<br/><i>Zona senza convenzionati</i>`
            + `<br/><small>Convenzionati più vicini nella tabella sotto la mappa</small>`)
          .on("click", () => setComuneSel(s))
          .addTo(layer.current);
      });
    }

    (soloScoperti && !ricerca ? [] : punti).forEach((p) => {
      const col = colore[p.commerciale_slug] || "#334155";
      const r = 6 + Math.sqrt(max ? p.importo / max : 0) * 16;
      const elenco = dealerSel
        ? p.dealer.filter((d) => d.codice_dealer === dealerSel.codice_dealer)
        : p.dealer;
      const lista = elenco.map((d) =>
        `<li><a href="/convenzionati/${d.codice_dealer}">${d.ragione_sociale}</a> · ${num(d.pratiche)} · ${eurShort(d.importo)}</li>`).join("");
      const m = L.circleMarker([p.lat, p.lng], { radius: ricerca ? Math.max(r, 10) : r, color: col,
        fillColor: col, fillOpacity: 0.55, weight: ricerca ? 4 : 2,
        dashArray: p.approssimato ? "4 3" : undefined })
        .bindTooltip(`${p.comune} · ${eurShort(p.importo)}${p.approssimato ? " · posizione approssimata" : ""}`)
        .bindPopup(`<b>${p.comune} (${p.provincia})${p.cap ? ` · CAP ${p.cap}` : ""}</b>`
          + `${p.approssimato ? '<br/><i>posizione approssimata: CAP o comune non riconosciuto</i>' : ""}`
          + `<br/><span style="color:${col}">${p.commerciale_nome}</span>`
          + `<br/>${num(p.pratiche)} pratiche · ${eurShort(p.importo)}<ul style="margin:6px 0 0 14px">${lista}</ul>`,
        { maxHeight: 240 })
        .addTo(layer.current);
      if (dealerSel && p.dealer.some((d) => d.codice_dealer === dealerSel.codice_dealer)) {
        mappa.current?.flyTo([p.lat, p.lng], 13, { duration: 0.8 });
        m.openPopup();
      }
    });

    if (capSel && punti.length) {
      mappa.current?.flyTo([punti[0].lat, punti[0].lng], 13, { duration: 0.8 });
    }
    if (!ricerca) mappa.current?.setView([45.6, 9.5], 9);
  }, [data, scoperti, soloScoperti, colore, puntiVisibili, capSel, dealerSel]);

  const vicini = (s) => {
    const R = 6371;
    const rad = (x) => (x * Math.PI) / 180;
    const km = (a, b, c, d) => {
      const dLat = rad(c - a); const dLon = rad(d - b);
      const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a)) * Math.cos(rad(c)) * Math.sin(dLon / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(h));
    };
    const out = [];
    (data?.punti || []).forEach((p) => p.dealer.forEach((dl) => out.push({
      ...dl, comune: p.comune, provincia: p.provincia,
      commerciale_nome: p.commerciale_nome, commerciale_slug: p.commerciale_slug,
      km: Math.round(km(s.lat, s.lng, p.lat, p.lng) * 10) / 10,
    })));
    return out.sort((a, b) => a.km - b.km).slice(0, 10);
  };

  // assegnazione sviluppo: commerciale del dealer piu vicino, con le eccezioni richieste
  const assegna = (s) => {
    const lista = vicini(s).filter((v) => !["gestione-hub", "altro"].includes(v.commerciale_slug));
    const primo = lista[0];
    if (!primo) return { auto: null, non_auto: null };
    const secondo = lista.find((v) => v.commerciale_slug !== primo.commerciale_slug);
    let auto = primo;
    let nonAuto = primo;
    if (primo.commerciale_slug === "linda-alfieri" && secondo) auto = secondo;
    if (primo.commerciale_slug === "francesco-accorra" && secondo) nonAuto = secondo;
    return { auto, non_auto: nonAuto };
  };

  const toggle = (slug) => setSel((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]));
  const punti = data?.punti || [];

  return (
    <div data-testid="mappa-page">
      <SectionTitle sub={data && soloScoperti
        ? `${num(data.scoperti?.length || 0)} comuni senza convenzionati in MI · BG · LO`
        : data
        ? `${num(punti.length)} punti · ${num(punti.reduce((a, p) => a + p.pratiche, 0))} pratiche · ${eurShort(punti.reduce((a, p) => a + p.importo, 0))} caricato ${ANNO}`
        : "Caricamento…"}>
        Mappa convenzionati
      </SectionTitle>

      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="mappa-ricerca">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-1">
            Cerca convenzionato
          </label>
          <div className="flex gap-2">
            <input className={`${searchCls} min-w-[280px]`} list="mappa-dealers" value={qDealer}
              data-testid="mappa-cerca-dealer" placeholder="Ragione sociale o codice…"
              onChange={(e) => setQDealer(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); cercaDealer(); } }} />
            <datalist id="mappa-dealers">
              {tuttiDealer.map((d) => (
                <option key={d.codice_dealer} value={d.ragione_sociale}>{`${d.comune} · ${d.cap}`}</option>
              ))}
            </datalist>
            <button type="button" onClick={cercaDealer} data-testid="mappa-cerca-dealer-btn"
              className="text-sm px-4 py-2 rounded-md bg-slate-900 text-white font-semibold
                hover:bg-slate-700 transition-colors duration-200">Trova</button>
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-1">
            Cerca comune
          </label>
          <div className="flex gap-2">
            <input className={`${searchCls} min-w-[220px]`} list="mappa-comuni" value={qCap}
              data-testid="mappa-cerca-comune" placeholder="es. Osio Sotto"
              onChange={(e) => setQCap(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); cercaComune(); } }} />
            <datalist id="mappa-comuni">
              {comuniDisponibili.map((c) => <option key={c} value={c} />)}
            </datalist>
            <button type="button" onClick={cercaComune} data-testid="mappa-cerca-comune-btn"
              className="text-sm px-4 py-2 rounded-md bg-sky-600 text-white font-semibold
                hover:bg-sky-700 transition-colors duration-200">Filtra</button>
          </div>
        </div>
        {(dealerSel || capSel) && (
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-3 py-1.5"
              data-testid="mappa-ricerca-attiva">
              {dealerSel
                ? `${dealerSel.ragione_sociale} · ${dealerSel.comune} (${dealerSel.provincia})`
                : `${capSel} · ${num(puntiVisibili.reduce((a, p) => a + p.dealer.length, 0))} convenzionati`}
            </span>
            <button type="button" onClick={azzeraRicerca} data-testid="mappa-ricerca-reset"
              className="text-xs text-slate-400 hover:text-slate-700 transition-colors duration-200">
              Azzera ricerca
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {(data?.commerciali || []).map((c) => (
          <button key={c.slug} type="button" onClick={() => toggle(c.slug)}
            data-testid={`mappa-filtro-${c.slug}`}
            className={`px-3 py-1.5 rounded-full text-xs border-2 transition-colors duration-200 ${
              sel.includes(c.slug) || !sel.length ? "text-white" : "bg-white text-slate-500"}`}
            style={sel.includes(c.slug) || !sel.length
              ? { backgroundColor: colore[c.slug], borderColor: colore[c.slug] }
              : { borderColor: colore[c.slug] }}>
            {c.nome}
          </button>
        ))}
        {sel.length > 0 && (
          <button type="button" onClick={() => setSel([])} data-testid="mappa-reset"
            className="px-3 py-1.5 rounded-full text-xs border-2 border-slate-300 text-slate-500 hover:border-slate-500 transition-colors duration-200">
            Tutti
          </button>
        )}
        <label className="ml-2 flex items-center gap-2 text-xs text-slate-500">
          <input type="checkbox" checked={scoperti || soloScoperti} disabled={soloScoperti}
            onChange={(e) => setScoperti(e.target.checked)} data-testid="mappa-toggle-scoperti" />
          Comuni scoperti MI · BG · LO ({num(data?.scoperti?.length || 0)})
        </label>
        <button type="button" onClick={() => setSoloScoperti((v) => !v)} data-testid="mappa-solo-scoperti"
          className={`px-3 py-1.5 rounded-full text-xs border-2 transition-colors duration-200 ${soloScoperti
            ? "bg-slate-500 border-slate-500 text-white"
            : "bg-white border-slate-300 text-slate-500 hover:border-slate-500"}`}>
          Solo comuni scoperti
        </button>
      </div>

      <Card className="p-0 overflow-hidden" data-testid="mappa-container">
        {/* mappa in scala di grigi: il filtro colpisce solo i tile, i marker restano a colori */}
        <style>{".leaflet-tile-pane{filter:grayscale(1) contrast(0.92) brightness(1.06);}"}</style>
        <div className="relative">
          <div ref={box} style={{ height: "70vh", width: "100%" }} />
          {(data?.senza_cap || []).length > 0 && (
            <div data-testid="mappa-fuori-sede"
              className="absolute left-3 bottom-3 z-[500] max-w-[280px] rounded-md border border-slate-300
                bg-white/95 backdrop-blur px-3 py-2 shadow-lg">
              <div className="uppercase text-[10px] tracking-[0.16em] font-semibold text-slate-500">
                Fuori mappa · {data.senza_cap.length} convenzionati
              </div>
              <div className="text-[10px] text-slate-400 mb-1.5">indirizzo non geolocalizzabile</div>
              <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
                {data.senza_cap.map((d) => (
                  <li key={d.codice_dealer} className="flex items-center gap-2 text-xs">
                    <span className="h-2 w-2 shrink-0 rounded-full ring-1 ring-white"
                      style={{ background: colore[d.commerciale_slug] || "#334155" }} />
                    <a href={`/convenzionati/${d.codice_dealer}`}
                      data-testid={`fuori-mappa-${d.codice_dealer}`}
                      className="truncate text-slate-700 hover:text-sky-700 hover:underline"
                      title={`${d.ragione_sociale} · ${d.commerciale_nome} · ${num(d.pratiche)} pratiche`}>
                      {d.ragione_sociale}
                    </a>
                    <span className="ml-auto font-mono text-slate-500 whitespace-nowrap">
                      {eurShort(d.importo)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Card>

      {capSel && (
        <Card className="p-6 mt-6 !border-2 !border-sky-500" data-testid="mappa-comune-elenco">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
            <div>
              <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">
                Convenzionati di {capSel}
              </h3>
              <p className="text-sm text-slate-500">
                {num(dealerComune.length)} convenzionati · {num(dealerComune.reduce((a, d) => a + d.pratiche, 0))} pratiche ·{" "}
                {eurShort(dealerComune.reduce((a, d) => a + (d.importo || 0), 0))} caricato {ANNO}
              </p>
            </div>
            <button type="button" onClick={azzeraRicerca} data-testid="mappa-comune-chiudi"
              className="text-xs text-slate-400 hover:text-slate-700 transition-colors duration-200">Chiudi</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 w-12">#</th>
                  <th className="text-left px-3 py-2">Convenzionato</th>
                  <th className="text-left px-3 py-2">Commerciale</th>
                  <th className="text-left px-3 py-2">CAP</th>
                  <th className="text-right px-3 py-2">Pratiche</th>
                  <th className="text-right px-3 py-2">Caricato</th>
                </tr>
              </thead>
              <tbody>
                {dealerComune.map((d, i) => (
                  <tr key={d.codice_dealer} className="border-t border-slate-100 hover:bg-slate-50 transition-colors duration-150">
                    <td className="px-3 py-2 font-mono text-xs text-slate-400">{i + 1}</td>
                    <td className="px-3 py-2">
                      <Link to={`/convenzionati/${d.codice_dealer}`} className="font-medium hover:underline">
                        {d.ragione_sociale}
                      </Link>
                    </td>
                    <td className="px-3 py-2" style={{ color: colore[d.commerciale_slug] }}>{d.commerciale_nome}</td>
                    <td className="px-3 py-2 font-mono text-slate-500">{d.cap}</td>
                    <td className="px-3 py-2 text-right font-mono">{num(d.pratiche)}</td>
                    <td className="px-3 py-2 text-right font-mono">{eurShort(d.importo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {comuneSel && (
        <Card className="p-6 mt-6 !border-2 !border-slate-400" data-testid="mappa-vicini">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
            <div>
              <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">
                Convenzionati più vicini a {comuneSel.comune} ({comuneSel.provincia})
              </h3>
              <p className="text-sm text-slate-500">
                CAP {comuneSel.cap}{comuneSel.popolazione ? ` · ${num(comuneSel.popolazione)} abitanti` : ""} · zona senza convenzionati
              </p>
            </div>
            <button type="button" onClick={() => setComuneSel(null)} data-testid="mappa-vicini-chiudi"
              className="text-xs text-slate-400 hover:text-slate-700 transition-colors duration-200">Chiudi</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 w-12">#</th>
                  <th className="text-left px-3 py-2">Convenzionato</th>
                  <th className="text-left px-3 py-2">Commerciale</th>
                  <th className="text-left px-3 py-2">Comune</th>
                  <th className="text-right px-3 py-2">Distanza</th>
                  <th className="text-right px-3 py-2">Pratiche</th>
                  <th className="text-right px-3 py-2">Caricato</th>
                </tr>
              </thead>
              <tbody>
                {vicini(comuneSel).map((v, i) => (
                  <tr key={v.codice_dealer} className="border-t border-slate-100 hover:bg-slate-50 transition-colors duration-150">
                    <td className="px-3 py-2 font-mono text-xs text-slate-400">{i + 1}</td>
                    <td className="px-3 py-2">
                      <Link to={`/convenzionati/${v.codice_dealer}`} className="font-medium hover:underline">
                        {v.ragione_sociale}
                      </Link>
                    </td>
                    <td className="px-3 py-2" style={{ color: colore[v.commerciale_slug] }}>{v.commerciale_nome}</td>
                    <td className="px-3 py-2 text-slate-500">{v.comune}</td>
                    <td className="px-3 py-2 text-right font-mono">{v.km} km</td>
                    <td className="px-3 py-2 text-right font-mono">{num(v.pratiche)}</td>
                    <td className="px-3 py-2 text-right font-mono">{eurShort(v.importo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {data?.scoperti?.length > 0 && (
        <Card className="p-6 mt-6" data-testid="mappa-classifica-scoperti">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-1">
            Comuni scoperti per popolazione · MI · BG · LO
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Zone senza convenzionati ordinate per abitanti. Lo sviluppo è assegnato al commerciale
            del convenzionato più vicino: se è Linda Alfieri l'auto passa al secondo commerciale,
            se è Francesco Accorrà il non auto passa al secondo commerciale.
          </p>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 w-14">#</th>
                  <th className="text-left px-3 py-2">Comune</th>
                  <th className="text-left px-3 py-2">Provincia</th>
                  <th className="text-left px-3 py-2">CAP</th>
                  <th className="text-right px-3 py-2">Abitanti</th>
                  <th className="text-left px-3 py-2">Convenzionato più vicino</th>
                  <th className="text-left px-3 py-2">Sviluppo auto</th>
                  <th className="text-left px-3 py-2">Sviluppo non auto</th>
                </tr>
              </thead>
              <tbody>
                {[...data.scoperti].sort((a, b) => (b.popolazione || 0) - (a.popolazione || 0))
                  .map((s, i) => {
                    const v = vicini(s)[0];
                    const a = assegna(s);
                    return (
                      <tr key={`${s.comune}-${s.cap}`} className="border-t border-slate-100 hover:bg-slate-50 transition-colors duration-150">
                        <td className="px-3 py-2 font-mono text-xs text-slate-400">{i + 1}</td>
                        <td className="px-3 py-2 font-medium">{s.comune}</td>
                        <td className="px-3 py-2 text-slate-500">{s.provincia}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-500">{s.cap}</td>
                        <td className="px-3 py-2 text-right font-mono">{num(s.popolazione)}</td>
                        <td className="px-3 py-2 text-slate-500">
                          {v ? `${v.ragione_sociale} · ${v.km} km` : "—"}
                        </td>
                        <td className="px-3 py-2 font-medium" style={{ color: colore[a.auto?.commerciale_slug] }}>
                          {a.auto ? `${a.auto.commerciale_nome} · ${a.auto.km} km` : "—"}
                        </td>
                        <td className="px-3 py-2 font-medium" style={{ color: colore[a.non_auto?.commerciale_slug] }}>
                          {a.non_auto ? `${a.non_auto.commerciale_nome} · ${a.non_auto.km} km` : "—"}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
